import { useState } from "react";
import { toast } from "sonner";
import { Palette, Plus, PencilSimple, Trash, Star } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { EmptyState } from "@/promotions/components/EmptyState";
import { CardsSkeleton } from "@/promotions/components/Skeleton";
import { ConfirmDialog } from "@/promotions/components/campaign/ConfirmDialog";
import { apiError } from "@/lib/api";
import { Modal, Field, TextInput, TextArea } from "../components/Modal";
import { useCreativeData, creativeApi } from "../useCreative";

const EMPTY = { name: "", palette: "", fonts: "", brand_voice: "", guidelines_text: "", negative_prompts: "", is_default: false };

export default function BrandKits() {
  const { brandKits, loading, reload } = useCreativeData();
  const [editing, setEditing] = useState(null); // {id?, ...form}
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const openCreate = () => setEditing({ ...EMPTY });
  const openEdit = (k) => setEditing({
    id: k.id,
    name: k.name || "",
    palette: (k.palette || []).join(", "),
    fonts: (k.fonts || []).join(", "),
    brand_voice: k.brand_voice || "",
    guidelines_text: k.guidelines_text || "",
    negative_prompts: (k.negative_prompts || []).join(", "),
    is_default: !!k.is_default,
  });

  const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name is required");
    setSaving(true);
    const body = {
      name: editing.name.trim(),
      palette: csv(editing.palette),
      fonts: csv(editing.fonts),
      brand_voice: editing.brand_voice.trim() || null,
      guidelines_text: editing.guidelines_text.trim() || null,
      negative_prompts: csv(editing.negative_prompts),
      is_default: editing.is_default,
    };
    try {
      if (editing.id) await creativeApi.updateBrandKit(editing.id, body);
      else await creativeApi.createBrandKit(body);
      toast.success(editing.id ? "Brand kit updated" : "Brand kit created");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await creativeApi.deleteBrandKit(toDelete.id);
      toast.success("Brand kit deleted");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="creative-brand-kits">
      <PromoPageHeader
        overline="Creative Studio"
        title="Brand Kits"
        description="Reusable visual identities that ground every generation in your brand."
        actions={
          <button onClick={openCreate} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90" data-testid="brand-kit-new">
            <Plus size={16} weight="bold" /> New brand kit
          </button>
        }
      />

      {loading ? (
        <CardsSkeleton count={3} />
      ) : brandKits.length === 0 ? (
        <EmptyState
          Icon={Palette}
          title="No brand kits yet"
          description="Create a brand kit to keep every generation on-brand."
          action={<button onClick={openCreate} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">New brand kit</button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="brand-kits-grid">
          {brandKits.map((k) => (
            <div key={k.id} className="flex flex-col rounded-lg border border-border bg-card p-5" data-testid={`brand-kit-${k.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium tracking-tight">{k.name}</h3>
                  {k.is_default && <span className="flex items-center gap-1 rounded-full bg-ai/15 px-2 py-0.5 text-[10px] font-medium text-ai"><Star size={10} weight="fill" /> Default</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(k)} aria-label="Edit brand kit" className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid={`brand-kit-edit-${k.id}`}>
                    <PencilSimple size={15} />
                  </button>
                  <button onClick={() => setToDelete(k)} aria-label="Delete brand kit" className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive" data-testid={`brand-kit-delete-${k.id}`}>
                    <Trash size={15} />
                  </button>
                </div>
              </div>

              {k.brand_voice && <p className="mt-1.5 text-sm text-muted-foreground">{k.brand_voice}</p>}

              {(k.palette || []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`palette-${k.id}`}>
                  {k.palette.map((c, i) => (
                    <span key={i} className="h-7 w-7 rounded-md border border-border" style={{ backgroundColor: c }} title={c} />
                  ))}
                </div>
              )}

              {(k.fonts || []).length > 0 && (
                <div className="mt-4 text-xs text-muted-foreground">Fonts: <span className="text-foreground">{k.fonts.join(", ")}</span></div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit brand kit" : "New brand kit"}
        description="Palette, fonts and negative prompts are comma-separated."
        size="lg"
        footer={
          <>
            <button onClick={() => setEditing(null)} className="rounded-md border border-border px-3.5 py-2 text-sm font-medium transition-colors hover:bg-accent" data-testid="brand-kit-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60" data-testid="brand-kit-save">{saving ? "Saving…" : "Save"}</button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <Field label="Name">
              <TextInput value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="Acme Corp" data-testid="brand-kit-input-name" />
            </Field>
            <Field label="Brand voice">
              <TextInput value={editing.brand_voice} onChange={(e) => setEditing((s) => ({ ...s, brand_voice: e.target.value }))} placeholder="Confident, minimal, premium" data-testid="brand-kit-input-voice" />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Color palette" hint="e.g. #0066FF, #F59E0B">
                <TextInput value={editing.palette} onChange={(e) => setEditing((s) => ({ ...s, palette: e.target.value }))} placeholder="#0066FF, #F59E0B" data-testid="brand-kit-input-palette" />
              </Field>
              <Field label="Fonts">
                <TextInput value={editing.fonts} onChange={(e) => setEditing((s) => ({ ...s, fonts: e.target.value }))} placeholder="Geist, Inter" data-testid="brand-kit-input-fonts" />
              </Field>
            </div>
            <Field label="Negative prompts" hint="Things to always avoid.">
              <TextInput value={editing.negative_prompts} onChange={(e) => setEditing((s) => ({ ...s, negative_prompts: e.target.value }))} placeholder="clutter, low quality" data-testid="brand-kit-input-negative" />
            </Field>
            <Field label="Guidelines">
              <TextArea rows={3} value={editing.guidelines_text} onChange={(e) => setEditing((s) => ({ ...s, guidelines_text: e.target.value }))} placeholder="Logo always top-left, generous whitespace…" data-testid="brand-kit-input-guidelines" />
            </Field>
            <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="brand-kit-input-default">
              <input type="checkbox" checked={editing.is_default} onChange={(e) => setEditing((s) => ({ ...s, is_default: e.target.checked }))} className="h-4 w-4 rounded border-border accent-brand" />
              Set as default brand kit
            </label>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        danger
        title="Delete brand kit?"
        description={toDelete ? `"${toDelete.name}" will be permanently removed.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
